// The YouTube upload options UX, shared by the single-post composer and the
// folder-import batch panel — the same shape TikTokPanel takes, for the same
// reason: YouTube's Terms of Service and API Services Developer Policies apply
// to EVERY surface that commits an upload, so both flows render this one
// component rather than each growing its own half of the rules.
//
// What lives here, and why:
//   * ToS §9.1        the upload notice, rendered verbatim beside the button
//                     that commits the upload (see `YouTubeUploadNotice`).
//   * ToS §9.1(iv)    the creator — never Broadside — designates whether the
//                     video is Made for Kids, BEFORE the upload.
//   * Policies III.C.1 user-provided values are never truncated, appended to or
//                     otherwise altered: the real limits are shown and enforced
//                     in the UI instead.
//   * Policies III.C.3 how each field is used on YouTube is stated outright.
//   * Policies III.E.3.f the channel the upload will land on is named here, on
//                     the screen where the upload is authorised.
//
// In the batch flow the creator makes ONE explicit Made-for-Kids choice that
// applies to every YouTube post in the import — still a manual designation,
// just not 37 of them.

/** Which flow the panel is rendered in — only affects the wording. */
export type YouTubeScope = "single" | "batch";

/** The creator's audience designation as the UI holds it. Deliberately allows
 * `""` ("nothing chosen yet"), which the wire shape (a boolean) cannot. */
export type MadeForKidsChoice = "" | "yes" | "no";

export interface YouTubeSettings {
  madeForKids: MadeForKidsChoice;
}

/** The only legal starting point: no audience designated. ToS §9.1(iv) makes
 * this the user's declaration, so there is no default to fall back on. */
export const EMPTY_YOUTUBE_SETTINGS: YouTubeSettings = { madeForKids: "" };

// --- YouTube's real, documented limits ---------------------------------------
//
// These are what the Data API actually enforces. They are mirrored in the
// adapter (netlify/functions/_lib/adapters/youtube.ts) so an over-limit value
// fails fast with the same wording instead of being cut down to fit.

/** `snippet.title` — 100 characters. */
export const YOUTUBE_TITLE_MAX_CHARS = 100;
/** `snippet.description` — 5000 BYTES, not characters. One emoji is 4 bytes and
 * a CJK character is 3, so a description well under 5000 characters can still
 * be rejected; measuring in UTF-16 units would miss exactly those posts. */
export const YOUTUBE_DESCRIPTION_MAX_BYTES = 5000;
/** `snippet.tags[]` — 500 characters in total across all tags. */
export const YOUTUBE_TAGS_MAX_CHARS = 500;

const TERMS_URL = "https://www.youtube.com/t/terms";

/** UTF-8 byte length — what YouTube counts a description in. */
export function youtubeByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

/** Total characters across every tag, which is what the 500 limit applies to. */
export function youtubeTagsLength(tags: string[]): number {
  return tags.reduce((total, tag) => total + tag.length, 0);
}

/** The YouTube-bound text of one post, as it would actually be published. */
export interface YouTubeDraftText {
  title: string;
  body: string;
  tags: string[];
}

/**
 * What the creator does about an over-limit value, which is not the same in both
 * flows: the single-post composer holds the value in an editable box on screen,
 * while a batch row's value came out of that folder's `post.yaml` and can only
 * be changed there. The measurement is identical either way — only the
 * instruction after it differs.
 */
const LIMIT_FIX: Record<YouTubeScope, { shorten: string; remove: string }> = {
  single: {
    shorten: "Shorten it before publishing.",
    remove: "Remove some before publishing.",
  },
  batch: {
    shorten: "Shorten it in this post's details file (post.yaml) and import the folder again.",
    remove: "Remove some in this post's details file (post.yaml) and import the folder again.",
  },
};

/**
 * Every way this post's text exceeds what YouTube accepts, in plain words.
 * Empty means it fits.
 *
 * Policies III.C.1 forbids "truncating, appending, or otherwise altering"
 * user-provided values, so an over-limit value is reported to the creator to
 * fix — it is never quietly shortened on their behalf.
 */
export function youtubeLimitProblems(
  text: YouTubeDraftText,
  scope: YouTubeScope = "single",
): string[] {
  const fix = LIMIT_FIX[scope];
  const problems: string[] = [];
  if (text.title.length > YOUTUBE_TITLE_MAX_CHARS) {
    problems.push(
      `The YouTube title is ${text.title.length} characters — YouTube allows ${YOUTUBE_TITLE_MAX_CHARS}. ${fix.shorten}`,
    );
  }
  const bytes = youtubeByteLength(text.body);
  if (bytes > YOUTUBE_DESCRIPTION_MAX_BYTES) {
    problems.push(
      `The YouTube description is ${bytes} bytes — YouTube allows ${YOUTUBE_DESCRIPTION_MAX_BYTES}. ${fix.shorten}`,
    );
  }
  const tagChars = youtubeTagsLength(text.tags);
  if (tagChars > YOUTUBE_TAGS_MAX_CHARS) {
    problems.push(
      `The YouTube tags total ${tagChars} characters — YouTube allows ${YOUTUBE_TAGS_MAX_CHARS} across all tags. ${fix.remove}`,
    );
  }
  return problems;
}

/** The first limit problem, or null. Drives the single-post publish gate. */
export function youtubeLimitBlocker(text: YouTubeDraftText): string | null {
  return youtubeLimitProblems(text, "single")[0] ?? null;
}

// Singular vs batch-wide phrasing. The strings differ only here so the two flows
// can't drift apart in meaning.
const COPY: Record<YouTubeScope, { audienceLabel: string; audiencePrompt: string }> = {
  single: {
    audienceLabel: "Is this video made for kids?",
    audiencePrompt: "Tell YouTube whether this video is made for kids",
  },
  batch: {
    audienceLabel: "Are these videos made for kids?",
    audiencePrompt: "Tell YouTube whether these videos are made for kids",
  },
};

/** What (if anything) about the creator's choices blocks an upload. Shared so
 * the batch gate and the single-post gate enforce exactly the same rule. */
export function youtubeSettingsBlocker(
  settings: YouTubeSettings,
  scope: YouTubeScope,
): string | null {
  if (!settings.madeForKids) return `${COPY[scope].audiencePrompt}.`;
  return null;
}

/** The wire value for the creator's designation, or null while unanswered. */
export function madeForKidsValue(settings: YouTubeSettings): boolean | null {
  if (!settings.madeForKids) return null;
  return settings.madeForKids === "yes";
}

// --- ToS §9.1 upload notice ---------------------------------------------------
//
// Required verbatim, on the screen where the user clicks upload. Held as three
// literals rather than JSX text so the wording cannot be reflowed by an editor
// or collapsed by JSX whitespace handling — the URL in the middle is a link, as
// the notice requires, and the rendered text still reads byte-for-byte.

const NOTICE_BEFORE_URL =
  "By clicking 'upload,' you certify that the content you are uploading complies with the YouTube Terms of Service (including the YouTube Community Guidelines) at ";
const NOTICE_AFTER_URL =
  ". Please be sure not to violate others' copyright or privacy rights.";

/** The full §9.1 wording, exactly as it must be shown. */
export const YOUTUBE_UPLOAD_NOTICE = `${NOTICE_BEFORE_URL}${TERMS_URL}${NOTICE_AFTER_URL}`;

/** The ToS §9.1 notice. Render this adjacent to the control that commits the
 * upload — in BOTH flows, since both of them upload. */
export function YouTubeUploadNotice() {
  return (
    <p className="muted small youtube-notice">
      {NOTICE_BEFORE_URL}
      <a href={TERMS_URL} target="_blank" rel="noreferrer">
        {TERMS_URL}
      </a>
      {NOTICE_AFTER_URL}
    </p>
  );
}

/** One "used / allowed" readout. `over` is what the creator sees instead of a
 * value being silently cut down to fit. */
function Counter({ label, used, max, unit }: {
  label: string;
  used: number;
  max: number;
  unit: string;
}) {
  const over = used > max;
  return (
    <li className={`youtube-counter ${over ? "over" : ""}`}>
      <span>{label}</span>
      <strong>
        {used} / {max} {unit}
      </strong>
    </li>
  );
}

export function YouTubePanel({
  channelName,
  scope,
  value,
  onChange,
  text,
  disabled = false,
}: {
  /** The connected channel this upload will land on (Policies III.E.3.f). */
  channelName: string;
  scope: YouTubeScope;
  value: YouTubeSettings;
  onChange: (next: YouTubeSettings) => void;
  /** The post's YouTube-bound text, for the live counters. Omitted in the batch
   * flow, where each row carries its own text and is flagged on its own row. */
  text?: YouTubeDraftText;
  /** Freezes every control while a batch is mid-flight. */
  disabled?: boolean;
}) {
  const copy = COPY[scope];
  // A stable name per scope keeps the two panels' radio groups independent when
  // both are somehow on screen.
  const groupName = `youtube-made-for-kids-${scope}`;

  return (
    <div className="card youtube-panel">
      <h2>YouTube upload options</h2>
      {scope === "batch" && (
        <p className="muted small">These apply to every YouTube upload in this batch.</p>
      )}

      {/* Policies III.E.3.f — name the channel that will receive the upload, on
          the screen where the upload is authorised. */}
      <div className="youtube-channel">
        <div className="conn-body">
          <div className="muted small">Uploading to channel</div>
          <div className="conn-name">{channelName}</div>
        </div>
      </div>

      {/* Policies III.C.3 — say outright how each field is used on YouTube. */}
      <div className="field">
        <span>How your post is used on YouTube</span>
        <ul className="youtube-mapping muted small">
          <li>
            <strong>Title</strong> becomes the video's title.
          </li>
          <li>
            <strong>Description / caption</strong> becomes the video's description.
          </li>
          <li>
            <strong>Tags</strong> become the video's tags.
          </li>
          <li>
            <strong>Visibility</strong> sets the video's privacy status on YouTube (private,
            unlisted or public).
          </li>
        </ul>
      </div>

      {/* ToS §9.1(iv) — the creator designates the audience before the upload.
          No option is preselected: Broadside must never make this declaration. */}
      <fieldset className="youtube-audience" disabled={disabled}>
        <legend>{copy.audienceLabel}</legend>
        <label className="youtube-audience-opt">
          <input
            type="radio"
            name={groupName}
            value="yes"
            checked={value.madeForKids === "yes"}
            onChange={() => onChange({ ...value, madeForKids: "yes" })}
          />
          <span>Yes, made for kids</span>
        </label>
        <label className="youtube-audience-opt">
          <input
            type="radio"
            name={groupName}
            value="no"
            checked={value.madeForKids === "no"}
            onChange={() => onChange({ ...value, madeForKids: "no" })}
          />
          <span>No, not made for kids</span>
        </label>
      </fieldset>
      <p className="muted small">
        Required by YouTube, and yours to decide — Broadside never answers this for you.
      </p>

      {/* Policies III.C.1 — the real limits, visible, so nothing has to be cut
          down behind the creator's back. */}
      {text && (
        <ul className="youtube-counters">
          <Counter
            label="Title"
            used={text.title.length}
            max={YOUTUBE_TITLE_MAX_CHARS}
            unit="characters"
          />
          <Counter
            label="Description"
            used={youtubeByteLength(text.body)}
            max={YOUTUBE_DESCRIPTION_MAX_BYTES}
            unit="bytes"
          />
          <Counter
            label="Tags"
            used={youtubeTagsLength(text.tags)}
            max={YOUTUBE_TAGS_MAX_CHARS}
            unit="characters"
          />
        </ul>
      )}
      {text && (
        <p className="muted small">
          YouTube measures the description in bytes, so emoji and non-Latin characters count for
          more than one. Anything over a limit is left exactly as you wrote it — shorten it
          yourself and Broadside will publish it unchanged.
        </p>
      )}
    </div>
  );
}
